import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, X, Sparkles } from 'lucide-react';
import { ErrorHandlingMedia } from '../common/ErrorHandlingMedia';
import { useImageGeneration } from '../../hooks/useImageRegeneration';
import { AISimpleGenerationModal } from '../modals/AISimpleGenerationModal';
import { 
  convertImageSizeToAspectRatio, 
  convertImageStyleToStyleType 
} from '../../services/imageService';
import type { ImageSize } from '../common/ImageSizeSelect';

interface MediaPostCardProps {
  media: File | null;
  onMediaChange: (file: File | null) => void;
  onClearMedia: () => void;
  existingMediaUrl?: string | null;
  existingMediaType?: 'image' | 'video' | null;
  format: 'regular' | 'reel';
  onFormatChange: (format: 'regular' | 'reel') => void;
  // Props for image generation
  brandId?: string;
  personaId?: string;
  content?: string;
  selectedPlatforms?: string[];
  onExistingMediaChange?: (url: string | null) => void;
}

export const MediaPostCard: React.FC<MediaPostCardProps> = ({
  media,
  onMediaChange,
  onClearMedia,
  existingMediaUrl,
  existingMediaType,
  format,
  onFormatChange,
  brandId,
  personaId,
  content = '',
  selectedPlatforms = [],
  onExistingMediaChange
}) => {
  const { t } = useTranslation();
  const [dragActive, setDragActive] = useState(false);
  
  // Image generation states
  const [isImageGenerationModalOpen, setIsImageGenerationModalOpen] = useState(false);
  const [temporaryGeneratedImageUrl, setTemporaryGeneratedImageUrl] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Image generation hook
  const imageGeneration = useImageGeneration();

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      onMediaChange(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      onMediaChange(e.target.files[0]);
    }
  };

  const handleRemove = () => {
    if (onClearMedia) {
      onClearMedia();
    }
    // Also clear existing media URL if the prop is available (for generated images)
    onExistingMediaChange?.(null);
  };

  // Handle image generation
  const handleImageGenerate = (imageStyle: string, imageSize: ImageSize) => {
    if (!brandId) {
      console.error('Missing required data for image generation');
      setErrors({
        submit: t('errors.missingBrand', 'Missing brand for image generation')
      });
      return;
    }

    const postContent = content || 'Generate an image';

    const request = {
      brandId,
      postContent: postContent, // Changed from 'prompt' to 'postContent' to match API expectation
      tone: 'professional', // Default tone, could be made configurable
      platforms: selectedPlatforms.length > 0 ? selectedPlatforms : ['instagram', 'linkedin'], // Use selected platforms or defaults
      aspect_ratio: convertImageSizeToAspectRatio(imageSize),
      style_type: convertImageStyleToStyleType(imageStyle)
    };

    imageGeneration.mutate(request, {
      onSuccess: (response) => {
        // Store the generated image temporarily for validation
        if (response.imageUrl) {
          setTemporaryGeneratedImageUrl(response.imageUrl);
        }
      },
      onError: (error) => {
        console.error('Image generation failed:', error);
        setErrors({
          submit: error.message || t('errors.imageGenerationFailed', 'Image generation failed')
        });
      }
    });
  };

  // Handle image validation (user confirms they want to use the new image)
  const handleImageValidation = (imageUrl: string) => {
    console.log('🟡 MediaPostCard: Using generated image URL directly:', imageUrl);
    
    // Use the generated image URL directly as existing media
    // We can't convert to File due to CORS restrictions from Ideogram
    onExistingMediaChange?.(imageUrl);
    
    // Clear any existing File media since we're using a URL instead
    onMediaChange(null);
    
    // Reset temporary state and close modal
    setTemporaryGeneratedImageUrl(null);
    setIsImageGenerationModalOpen(false);
    
    console.log('🟡 MediaPostCard: Generated image set as existing media URL');
  };

  // Check if image generation is available
  const canGenerateImage = Boolean(brandId);

  return (
    <div className="bg-white rounded-xl shadow-md p-6 border border-black">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900">
          {t('mediaPost.title', 'Média')}
        </h2>
        {canGenerateImage && (
          <button
            onClick={() => setIsImageGenerationModalOpen(true)}
            disabled={imageGeneration.isPending}
            className="inline-flex items-center px-3 py-1.5 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {imageGeneration.isPending ? (
              <>
                <div className="w-4 h-4 mr-1.5 border-2 border-t-transparent border-white rounded-full animate-spin"></div>
                {t('common.generating', 'Generating...')}
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-1.5" />
                {t('actions.generateImage', 'Generate an image')}
              </>
            )}
          </button>
        )}
      </div>

      {/* Media Upload/Preview Section */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-gray-700">{t('postContent.media', 'Media')}</h3>
        </div>
        {media || existingMediaUrl ? (
          <div className="relative group">
            {media ? (
              <ErrorHandlingMedia
                src={URL.createObjectURL(media)}
                alt="Preview"
                type={media.type.startsWith('image/') ? 'image' : 'video'}
                className="w-full h-64 object-contain rounded-lg bg-gray-100"
                controls={!media.type.startsWith('image/')}
              />
            ) : existingMediaUrl ? (
              <ErrorHandlingMedia
                src={existingMediaUrl}
                alt="Preview"
                type={existingMediaType || 'image'}
                className="w-full h-64 object-contain rounded-lg bg-gray-100"
                controls={existingMediaType === 'video'}
              />
            ) : null}
            <button
              onClick={handleRemove}
              className="absolute top-2 right-2 p-1.5 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75 transition-opacity opacity-0 group-hover:opacity-100"
              title={t('postContent.removeMediaTooltip', 'Remove media')}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 ${dragActive ? 'border-primary-500' : 'border-gray-300'} border-dashed rounded-md h-64 items-center text-center relative`}
          >
            <input
              id="file-upload"
              name="file-upload"
              type="file"
              className="sr-only"
              onChange={handleChange}
              accept="image/*,video/*"
            />
            <label
              htmlFor="file-upload"
              className="w-full h-full flex flex-col items-center justify-center cursor-pointer"
            >
              <Upload className={`mx-auto h-12 w-12 ${dragActive ? 'text-primary-500' : 'text-gray-400'}`} />
              <span className={`mt-2 block text-sm font-medium ${dragActive ? 'text-primary-600' : 'text-gray-900'}`}>
                {t('postContent.dropMediaHere', 'Drop your media here or click to upload')}
              </span>
              <p className={`mt-1 text-xs ${dragActive ? 'text-primary-500' : 'text-gray-500'}`}>
                {t('postContent.supportedFormats', 'Supports images and videos up to 10MB')}
              </p>
            </label>
          </div>
        )}
      </div>

      {/* Formats Section */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">{t('postContent.formats', 'Format')}</h4>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onFormatChange('regular')}
            className={`inline-flex items-center px-3 py-1 text-xs rounded-full border ${
              format === 'regular'
                ? 'bg-primary-100 text-primary-700 border-primary-600'
                : 'bg-gray-100 text-gray-700 border-black hover:bg-gray-200'
            }`}
          >
            <span className="mr-1">📸</span>
            {t('postContent.regularFormat', 'Classic (Horizontal)')}
          </button>
          <button
            onClick={() => onFormatChange('reel')}
            className={`inline-flex items-center px-3 py-1 text-xs rounded-full border ${
              format === 'reel'
                ? 'bg-primary-100 text-primary-700 border-primary-600'
                : 'bg-gray-100 text-gray-700 border-black hover:bg-gray-200'
            }`}
          >
            <span className="mr-1">📱</span>
            {t('postContent.reelFormat', 'Reel (Vertical)')}
          </button>
        </div>
      </div>

      {/* Error Display */}
      {errors.submit && (
        <div className="mt-3 bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-600">{errors.submit}</p>
        </div>
      )}

      {/* Image Generation Modal */}
      <AISimpleGenerationModal
        isOpen={isImageGenerationModalOpen}
        onClose={() => {
          setIsImageGenerationModalOpen(false);
          setTemporaryGeneratedImageUrl(null);
        }}
        onGenerate={handleImageGenerate}
        onValidate={handleImageValidation}
        isLoading={imageGeneration.isPending}
        currentImageStyle="auto"
        currentImageSize="square"
        generatedImageUrl={temporaryGeneratedImageUrl}
      />
    </div>
  );
}; 